import type { Document } from "@/types/documents";
import { reportDocumentTableFailure } from "./documentTableFailure";
import type {
  CategoryTag,
  DesignDisciplineTag,
  DesignStageTag,
  DocumentWithTags,
} from "./documentTableTypes";
import { buildValidFilterIdSet } from "./documentTableValues";

import type { documentTableOptions } from "./documentTableOptions";
import type { useTableComments } from "./useTableComments";
import type { useTableDialogState } from "./useTableDialogState";
type Options = Pick<
  ReturnType<typeof useTableDialogState>,
  "setChildModalDocId" | "setChildModalFilename" | "setTeachFranklinDoc" | "setTeachFranklinOpen"
> &
  Pick<ReturnType<typeof documentTableOptions>, "store" | "buildingId"> &
  Pick<ReturnType<typeof useTableComments>, "docFiltersContext">;
export function createTableTeaching(options: Options) {
  const {
    setChildModalDocId,
    setChildModalFilename,
    store,
    setTeachFranklinDoc,
    setTeachFranklinOpen,
    buildingId,
    docFiltersContext,
  } = options;
  // Handler for showing child documents
  const handleShowChildren = (docId: string, filename: string) => {
    setChildModalDocId(docId);
    setChildModalFilename(filename);
  };

  // Handler for Teach Franklin (tag correction)
  const handleTeachFranklin = (doc: DocumentWithTags) => {
    // Get full document from store (which has categories, disciplines, stages)
    const storeDoc = store?.documents.get(doc.id);
    const fullDoc = storeDoc || doc;

    const documentForModal = teachingDocument(fullDoc);
    setTeachFranklinDoc(documentForModal);
    setTeachFranklinOpen(true);
  };

  // Handler for document update from Teach Franklin modal
  const handleDocumentUpdate = (updatedDoc: Document) => {
    // The modal has already saved to DB and constructed updatedDoc from known data.
    // We delay the server refresh slightly to avoid race conditions where
    // an immediate fetch might return stale data before DB commit completes.
    // The realtime subscription will also eventually sync this change.
    if (store && updatedDoc.id) {
      const docId = parseInt(updatedDoc.id, 10);
      if (!isNaN(docId)) {
        // Delay refresh to let DB transaction fully complete
        setTimeout(() => {
          store.refreshSingleDocument(docId);
        }, 500);
      }

      // Refresh available filters so tag counts update after tag changes.
      // Also clean up any active tag filters that no longer exist in the
      // refreshed filter set (e.g., a category that now has 0 documents).
      if (buildingId) {
        // Small delay to let DB transaction commit
        setTimeout(async () => {
          try {
            await store.loadAvailableFilters(buildingId);

            // Remove stale active tag filters
            if (docFiltersContext && store.availableFilters) {
              const currentActive = docFiltersContext.filters.activeTagFilters;
              if (currentActive.length > 0) {
                const validIds = buildValidFilterIdSet(store.availableFilters);
                const cleaned = currentActive.filter((id) => validIds.has(id));
                if (cleaned.length !== currentActive.length) {
                  docFiltersContext.setActiveTagFilters(cleaned);
                }
              }
            }
          } catch (e) {
            reportDocumentTableFailure("filters_refresh", e);
          }
        }, 500);
      }
    }
  };
  return { handleShowChildren, handleTeachFranklin, handleDocumentUpdate };
}

function teachingDocument(
  fullDoc:
    | DocumentWithTags
    | NonNullable<ReturnType<NonNullable<Options["store"]>["documents"]["get"]>>
): Document {
  // Convert to Document type for the modal
  const documentForModal: Document = {
    id: fullDoc.id,
    filename: fullDoc.filename,
    file_path: fullDoc.file_path,
    building_id: fullDoc.building_id,
    content_type: fullDoc.content_type,
    size: fullDoc.size,
    created_at: fullDoc.created_at,
    parent_document_id: fullDoc.parent_document_id,
    version: fullDoc.version ?? undefined,
    created_by_name: fullDoc.created_by_name ?? undefined,
    state: fullDoc.state as Document["state"],
    categories: fullDoc.categories?.map((c: CategoryTag) => ({
      id: c.id,
      name: c.name,
      name_et: c.name_et,
    })),
    design_disciplines: fullDoc.design_disciplines?.map((d: DesignDisciplineTag) => ({
      id: d.id,
      name: d.name,
      name_en: d.name_en,
      abbreviation: d.abbreviation,
      description: d.description,
    })),
    design_stages: fullDoc.design_stages?.map((s: DesignStageTag) => ({
      id: s.id,
      name: s.name,
      name_en: s.name_en,
      abbreviation: s.abbreviation,
    })),
  };
  return documentForModal;
}
